/** Shared GoodLeap application URL resolution (Trinity `LoanStatus.Link` + API payload shapes). */

export function mergeApiShape(o: Record<string, unknown>): Record<string, unknown> {
    const raw = o.Data ?? o.data ?? o.Result ?? o.result ?? o.Payload ?? o.payload;
    if (typeof raw === 'string' && raw.trim().startsWith('{')) {
        try {
            const parsed = JSON.parse(raw) as Record<string, unknown>;
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                return { ...o, ...parsed };
            }
        } catch {
            /* ignore */
        }
    }
    const inner = raw as Record<string, unknown> | undefined;
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
        return { ...o, ...inner };
    }
    return o;
}

export function collectHttpsUrls(v: unknown, depth = 0, acc: string[] = []): string[] {
    if (depth > 14 || v == null) return acc;
    if (typeof v === 'string') {
        const matches = v.match(/https?:\/\/[^\s"'<>]+/gi);
        if (matches) acc.push(...matches.map((m) => m.replace(/[,;.)]+$/, '')));
        return acc;
    }
    if (typeof v !== 'object') return acc;
    if (Array.isArray(v)) {
        for (const x of v) collectHttpsUrls(x, depth + 1, acc);
        return acc;
    }
    for (const x of Object.values(v as Record<string, unknown>)) collectHttpsUrls(x, depth + 1, acc);
    return acc;
}

/** Resolve GoodLeap / TPO application URL from `LoanStatus`, API payloads, or nested .NET `Data` shapes. */
export function pickGoodLeapApplicationUrl(source: unknown, depth = 0): string | null {
    if (depth > 5 || source == null) return null;
    if (typeof source === 'string') {
        const t = source.trim();
        return /^https?:\/\//i.test(t) ? t : null;
    }
    if (typeof source !== 'object') return null;
    const root = source as Record<string, unknown>;
    const flat = mergeApiShape(root);
    const keyOrder = [
        'Link',
        'link',
        'Url',
        'url',
        'ApplicationUrl',
        'applicationUrl',
        'SubmitApplicationUrl',
        'LoanApplicationUrl',
        'TpoUrl',
        'tpoUrl',
        'IFrameUrl',
        'iFrameUrl',
    ];
    for (const k of keyOrder) {
        const c = flat[k];
        if (typeof c === 'string') {
            const t = c.trim();
            if (/^https?:\/\//i.test(t)) return t;
        }
    }
    const loan = flat.LoanStatus ?? flat.loanStatus;
    if (loan != null && loan !== root) {
        const nested = pickGoodLeapApplicationUrl(loan, depth + 1);
        if (nested) return nested;
    }
    const urls = collectHttpsUrls(source, 0, []);
    const preferred = urls.find((u) => /goodleap|glue|tpo\/|\/loans\//i.test(u));
    return preferred ?? urls[0] ?? null;
}
